import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { AuthLayout } from "@/components/AuthLayout";
import { useAuth } from "@/context/AuthContext";
import { api, errorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Loader2, MailCheck, AlertTriangle } from "lucide-react";

/** /bekreft-epost?token=… – bekrefter e-postadressen (fungerer innlogget og utlogget). */
export default function VerifyEmailPage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState({ phase: "working", email: "", invitations: 0, error: "" });
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    if (!token) {
      setState({ phase: "error", error: "Lenken mangler bekreftelseskode." });
      return;
    }
    api.post("/auth/verify-email", { token }, { skipActAs: true })
      .then(async (r) => {
        setState({ phase: "done", email: r.data.email, invitations: r.data.pending_invitations || 0, error: "" });
        if (user) await refreshUser();
      })
      .catch((e) => setState({ phase: "error", error: errorMessage(e, "Kunne ikke bekrefte e-postadressen") }));
  }, [token, user, refreshUser]);

  if (state.phase === "working") {
    return (
      <AuthLayout title="Bekrefter e-postadressen" subtitle="Et øyeblikk …">
        <div className="flex items-center justify-center py-10" data-testid="verify-email-loading"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      </AuthLayout>
    );
  }

  if (state.phase === "error") {
    return (
      <AuthLayout title="Bekreftelse mislyktes" subtitle="Lenken kunne ikke brukes." footer={<p><Link to={user ? "/innstillinger" : "/logg-inn"} className="text-primary font-medium hover:underline" data-testid="verify-email-back">{user ? "Til innstillinger" : "Til innlogging"}</Link></p>}>
        <Alert variant="destructive" data-testid="verify-email-error">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>E-postadressen ble ikke bekreftet</AlertTitle>
          <AlertDescription>{state.error} {user ? "Du kan be om en ny bekreftelseslenke fra banneret øverst i appen." : "Logg inn og be om en ny bekreftelseslenke."}</AlertDescription>
        </Alert>
      </AuthLayout>
    );
  }

  const next = state.invitations > 0 ? "/familie" : "/";
  return (
    <AuthLayout title="E-postadressen er bekreftet" subtitle={`${state.email} er nå bekreftet.`}>
      <div className="rounded-lg border bg-secondary/60 px-4 py-3 flex items-start gap-3" data-testid="verify-email-success">
        <div className="h-9 w-9 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0"><MailCheck className="h-5 w-5" /></div>
        <div className="text-sm">
          <p className="font-medium">Takk! Kontoen din er klar.</p>
          <p className="text-muted-foreground" data-testid="verify-email-invitations">
            {state.invitations > 0 ? `${state.invitations === 1 ? "1 invitasjon til fullmakt er" : `${state.invitations} invitasjoner til fullmakt er`} koblet til kontoen din og venter på svar.` : "Ingen invitasjoner ventet på denne adressen."}
          </p>
        </div>
      </div>
      <Button className="w-full h-11 mt-5" onClick={() => navigate(user ? next : `/logg-inn`, { replace: true })} data-testid="verify-email-continue">
        {user ? (state.invitations > 0 ? "Gå til Familie og fullmakt" : "Gå til oversikten") : "Logg inn"}
      </Button>
    </AuthLayout>
  );
}
